import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { ADMIN_ORGANIZATION_ID, API_BASE_URL, API_ENDPOINTS } from '../config/api';
import { hasOrganizationAccess } from '../utils/auth';

const ReportDataContext = createContext();

export const useReportData = () => {
  const context = useContext(ReportDataContext);
  if (!context) {
    throw new Error('useReportData must be used within a ReportDataProvider');
  }
  return context;
};

export const ReportDataProvider = ({ children }) => {
  // Core data states
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sensorSummaryData, setSensorSummaryData] = useState([]);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Image cache states
  const [popupImageCache, setPopupImageCache] = useState({});
  const [sensorImageCache, setSensorImageCache] = useState({});
  
  // Use ref to prevent multiple concurrent calls
  const loadingRef = useRef(false);

  // Main data loading function
  const loadReportData = useCallback(async (selectedOrgId, startDate, endDate, zoomLevel) => {
    console.log('🚀 ReportDataProvider loadReportData called with:', { selectedOrgId, startDate, endDate, zoomLevel });
    
    if (!selectedOrgId || selectedOrgId === 'undefined' || selectedOrgId === undefined || selectedOrgId === '') {
      console.log('❌ No selectedOrgId or selectedOrgId is invalid, returning early');
      return;
    }

    if (!startDate || !endDate) {
      console.log('❌ No startDate or endDate, returning early');
      return;
    }

    if (loadingRef.current) {
      console.log('⏳ Already loading, skipping duplicate call');
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    // Create AbortController for request cancellation
    const abortController = new AbortController();

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      console.log('🚀 Loading report data with parallel requests for organization:', selectedOrgId);
      const isAdmin = hasOrganizationAccess(ADMIN_ORGANIZATION_ID);
      console.log('🔐 User has admin access:', isAdmin);
      
      const baseParams = {
        startDate: startDate,
        endDate: endDate,
        binning: zoomLevel
      };

      const headers = {
        'Authorization': `Bearer ${token}`
      };

      const requestConfig = {
        headers,
        signal: abortController.signal
      };

      // Define all report endpoints to load in parallel
      const cardParams = {
        startDate,
        endDate
      };

      const reportPromises = [
        // Public report endpoints (always load)
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.REPORT_DAILY_CONSUMPTION(selectedOrgId)}`, { ...requestConfig, params: baseParams }),
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.SENSOR_SUMMARY(selectedOrgId)}`, requestConfig),
        // Public card endpoints (always load)
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.CARDS_TOTAL_DEVICES(selectedOrgId)}`, { ...requestConfig, params: cardParams }),
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.CARDS_DEVICES_WITH_DATA(selectedOrgId)}`, { ...requestConfig, params: cardParams }),
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.CARDS_TOTAL_USAGE(selectedOrgId)}`, { ...requestConfig, params: cardParams })
      ];

      // Add admin-only endpoints if user has admin access
      if (isAdmin) {
        reportPromises.push(
          // Admin-only card endpoints
          axios.get(`${API_BASE_URL}${API_ENDPOINTS.ORGANIZATIONS.CARDS_AVERAGE_USAGE(selectedOrgId)}`, { 
            ...requestConfig, 
            params: { ...baseParams } 
          })
        );
      }

      console.log(`📊 Loading ${reportPromises.length} report endpoints in parallel...`);
      
      // Execute all requests in parallel
      const results = await Promise.allSettled(reportPromises);
      
      // Process results
      const [dailyConsumption, sensorSummary, totalDevicesCard, devicesWithDataCard, totalUsageCard, averageUsageCard] = results;

      // Handle daily consumption data (primary data)
      if (dailyConsumption.status === 'fulfilled') {
        console.log('✅ Daily consumption data loaded:', dailyConsumption.value.data);
        setReportData(dailyConsumption.value.data.devices || []);
        setSummary(dailyConsumption.value.data.summary || {});
      } else {
        console.error('❌ Failed to load daily consumption:', dailyConsumption.reason);
        throw new Error('Failed to load primary report data');
      }

      // Handle sensor summary data
      if (sensorSummary.status === 'fulfilled') {
        console.log('✅ Sensor summary data loaded:', sensorSummary.value.data);
        setSensorSummaryData(sensorSummary.value.data.sensors || []);
      } else {
        console.error('❌ Failed to load sensor summary:', sensorSummary.reason);
      }

      // Handle card data
      const cardData = {};
      
      if (totalDevicesCard.status === 'fulfilled') {
        console.log('✅ Total devices card loaded:', totalDevicesCard.value.data);
        const payload = totalDevicesCard.value.data || {};
        cardData.totalDevices = payload.totalDevices ?? payload.count ?? 0;
        cardData.totalDevicesTimestamp = payload.timestamp || null;
      } else {
        console.error('⚠️ Failed to load total devices card:', totalDevicesCard.reason);
      }

      if (devicesWithDataCard.status === 'fulfilled') {
        console.log('✅ Devices with data card loaded:', devicesWithDataCard.value.data);
        const payload = devicesWithDataCard.value.data || {};
        cardData.devicesWithData = payload.devicesWithData ?? payload.connectedDevices ?? 0;
        cardData.devicesWithDataPercentage = payload.dataPercentage ?? null;
      } else {
        console.error('⚠️ Failed to load devices with data card:', devicesWithDataCard.reason);
      }

      if (totalUsageCard.status === 'fulfilled') {
        console.log('✅ Total usage card loaded:', totalUsageCard.value.data);
        const payload = totalUsageCard.value.data || {};
        cardData.totalUsage = payload.totalUsage ?? 0;
        cardData.totalUsageUnit = payload.unit || payload.units || null;
      } else {
        console.error('⚠️ Failed to load total usage card:', totalUsageCard.reason);
      }

      // Handle admin-only data (optional)
      if (isAdmin) {
        if (averageUsageCard?.status === 'fulfilled') {
          console.log('✅ Admin average usage card loaded:', averageUsageCard.value.data);
          // Map backend field names to frontend expected field names
          cardData.avgUsagePerDayPerSensor = averageUsageCard.value.data.averageUsagePerDay;
          cardData.avgUsagePerDevice = averageUsageCard.value.data.averageUsagePerDevice;
          cardData.unit = averageUsageCard.value.data.unit;
        } else if (averageUsageCard?.status === 'rejected') {
          console.error('⚠️ Failed to load admin average usage card:', averageUsageCard.reason);
        }
      }

      // Merge card data into summary
      setSummary(prevSummary => ({
        ...prevSummary,
        ...cardData
      }));

      // Store card data in state (you may need to add state variables for cards)
      console.log('🎯 Card data loaded:', cardData);

      console.log('✅ Parallel report data loading completed');
    } catch (err) {
      console.error('❌ Error loading report data:', err);
      // Don't set error state for aborted requests
      if (err.name !== 'AbortError' && !abortController.signal.aborted) {
        setError(err.response?.data?.error || err.message || 'Failed to load report data');
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
        loadingRef.current = false;
      }
    }

    // Return cleanup function for request cancellation
    return () => {
      abortController.abort();
      loadingRef.current = false;
    };
  }, []);

  // Image cache management functions
  const updatePopupImageCache = useCallback((deviceId, cacheData) => {
    setPopupImageCache(prev => ({
      ...prev,
      [deviceId]: cacheData
    }));
  }, []);

  const updateSensorImageCache = useCallback((deviceId, imageData) => {
    setSensorImageCache(prev => ({
      ...prev,
      [deviceId]: imageData
    }));
  }, []);

  // Clear data function
  const clearReportData = useCallback(() => {
    setReportData([]);
    setSummary(null);
    setSensorSummaryData([]);
    setError(null);
    setPopupImageCache({});
    setSensorImageCache({});
  }, []);

  const contextValue = {
    // Data states
    reportData,
    summary,
    sensorSummaryData,
    isLoading,
    error,
    
    // Image cache states
    popupImageCache,
    sensorImageCache,
    
    // Actions
    loadReportData,
    updatePopupImageCache,
    updateSensorImageCache,
    clearReportData
  };

  return (
    <ReportDataContext.Provider value={contextValue}>
      {children}
    </ReportDataContext.Provider>
  );
};
